import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

interface ArticleLayoutProps {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  tag: string;
  datePublished: string;
  slug: string;
  children: React.ReactNode;
}

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gtbuy.wtf';

export default function ArticleLayout({
  title,
  description,
  image,
  imageAlt,
  tag,
  datePublished,
  slug,
  children,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const articleUrl = `${siteUrl}/article/${slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image,
    datePublished,
    dateModified: datePublished,
    author: { '@type': 'Organization', name: 'GTBuy Sheet' },
    publisher: { '@type': 'Organization', name: 'GTBuy Sheet', url: siteUrl },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
      url: articleUrl,
    },
  };

  return (
    <main>
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="flex items-center justify-between h-16 px-4 md:px-10 max-w-screen-2xl mx-auto">
          <Link
            to="/"
            className="text-lg md:text-xl font-heading font-semibold tracking-tight text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
          >
            gtbuy.wtf
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link
              to="/"
              className="text-xs md:text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/article/gtbuy-spreadsheet-guide"
              className="text-xs md:text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap hidden sm:block"
            >
              Shopping Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-3 md:px-4 py-2 text-xs md:text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              buy it now
            </a>
          </div>
        </div>
      </nav>

      <article className="py-16 md:py-24 px-4 md:px-10 bg-background-50">
        <div className="max-w-screen-lg mx-auto">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full tracking-wide">
                {tag}
              </span>
              <span className="text-xs text-foreground-500">{datePublished}</span>
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
              {description}
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12">
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-56 md:h-80 object-cover object-top"
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            <div className="flex-1">
              {children}

              <section className="mt-12 p-6 bg-accent-50 rounded-2xl border border-accent-200/50">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">
                  Ready to Start Shopping?
                </h3>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                  Explore all 14 categories and discover your next favorite product. Click below to visit the main store, or return to the <Link to="/" className="text-accent-600 hover:text-accent-700 underline">GTBuy Sheet homepage</Link> to browse by category.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-center"
                  >
                    buy it now
                    <i className="ri-arrow-right-up-line ml-2 text-sm"></i>
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-3 text-sm font-medium text-foreground-700 border border-foreground-300 rounded-full hover:bg-foreground-100 transition-all whitespace-nowrap cursor-pointer text-center"
                  >
                    ← Back to Homepage
                  </Link>
                </div>
              </section>
            </div>

            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="p-5 bg-background-100 rounded-xl">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-3">Quick Category Links</h4>
                  <ul className="space-y-1.5">
                    {categories.map((cat) => (
                      <li key={cat.slug}>
                        <a
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="text-xs text-foreground-600 hover:text-accent-500 transition-colors flex items-center gap-2"
                        >
                          <i className="ri-arrow-right-s-line text-accent-400"></i>
                          {cat.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 bg-primary-500 rounded-xl text-background-50">
                  <h4 className="text-sm font-semibold mb-2">Visit Our Main Store</h4>
                  <p className="text-xs text-background-50/80 leading-relaxed mb-4">
                    Browse thousands of products across all categories
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-center px-4 py-2.5 text-xs font-semibold bg-background-50 text-primary-600 rounded-full hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                  >
                    gtbuy sheet →
                  </a>
                </div>

                <div className="p-5 bg-background-100 rounded-xl">
                  <h4 className="text-sm font-semibold text-foreground-900 mb-3">More Articles</h4>
                  <ul className="space-y-2">
                    <li>
                      <Link to="/article/gtbuy-spreadsheet-guide" className="text-xs text-foreground-600 hover:text-accent-500 transition-colors flex items-center gap-2">
                        <i className="ri-article-line text-accent-400"></i>
                        Complete Guide
                      </Link>
                    </li>
                    <li>
                      <Link to="/article/gtbuy-shopping-tips-2026" className="text-xs text-foreground-600 hover:text-accent-500 transition-colors flex items-center gap-2">
                        <i className="ri-lightbulb-line text-accent-400"></i>
                        Shopping Tips
                      </Link>
                    </li>
                    <li>
                      <Link to="/article/gtbuy-sheet-beginners" className="text-xs text-foreground-600 hover:text-accent-500 transition-colors flex items-center gap-2">
                        <i className="ri-user-star-line text-accent-400"></i>
                        Beginner Guide
                      </Link>
                    </li>
                    <li>
                      <Link to="/article/gtbuy-sheet-faq" className="text-xs text-foreground-600 hover:text-accent-500 transition-colors flex items-center gap-2">
                        <i className="ri-question-answer-line text-accent-400"></i>
                        FAQ
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <footer className="py-12 px-4 md:px-10 bg-foreground-900 text-background-50">
        <div className="max-w-screen-lg mx-auto text-center">
          <Link to="/" className="text-lg font-heading font-semibold tracking-tight hover:text-accent-400 transition-colors">
            gtbuy.wtf
          </Link>
          <div className="mt-3 flex items-center justify-center gap-6 flex-wrap">
            <Link to="/" className="text-xs text-background-50/50 hover:text-accent-400 transition-colors">Home</Link>
            <Link to="/article/gtbuy-spreadsheet-guide" className="text-xs text-background-50/50 hover:text-accent-400 transition-colors">Shopping Guide</Link>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-background-50/50 hover:text-accent-400 transition-colors">Main Store</a>
          </div>
          <p className="mt-6 text-xs text-background-50/30">
            &copy; {new Date().getFullYear()} gtbuy.wtf. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
}